import { useState } from "react";
import { Heart, Calculator, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";

const TIP_TIERS = [
  { label: "Appreciate",  pct: 0.15, emoji: "🤍", note: "For a great session" },
  { label: "Love it",     pct: 0.20, emoji: "💜", note: "The industry standard" },
  { label: "Amazing",     pct: 0.25, emoji: "💛", note: "For exceptional work" },
];

function tipAmount(midpoint: number, pct: number) {
  return Math.round(midpoint * pct);
}

interface TipGuideProps {
  low: number;
  high: number;
}

export function TipGuide({ low, high }: TipGuideProps) {
  const midpoint = Math.round((low + high) / 2);
  const [selectedPct, setSelectedPct] = useState<number | null>(null);

  const selectedTier = TIP_TIERS.find(t => t.pct === selectedPct) ?? null;
  const selectedTip  = selectedTier ? tipAmount(midpoint, selectedTier.pct) : 0;
  const budgetTotal  = midpoint + selectedTip;

  return (
    <div
      className="rounded-xl p-5 bg-white/[0.03] border border-white/10 mt-6"
      data-testid="tip-guide"
    >
      <div className="flex items-center gap-2 mb-3">
        <Heart size={14} className="text-rose-400 fill-rose-400" />
        <span className="text-xs uppercase tracking-[0.2em] text-white/60 font-subheading font-bold">
          Thinking About a Tip?
        </span>
      </div>

      <p className="text-xs text-white/50 leading-relaxed mb-4">
        Tipping is optional but always appreciated — it goes directly to your artist.
        Tattoo artists invest real time before your session: sketching designs, sourcing
        references, and prepping your stencil, often unpaid. A tip is a meaningful way to say thank you.
      </p>

      <AnimatePresence mode="wait">
        {!selectedTier ? (
          /* ── Tier picker ── */
          <motion.div
            key="tier-picker"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.18 }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="tip-tiers">
              {TIP_TIERS.map((tier) => {
                const tip = tipAmount(midpoint, tier.pct);
                return (
                  <button
                    key={tier.label}
                    data-testid={`tip-tier-${tier.label.toLowerCase().replace(" ", "-")}`}
                    onClick={() => setSelectedPct(tier.pct)}
                    className="rounded-lg p-3 text-center transition-all duration-200 active:scale-[0.96] border bg-white/[0.04] border-white/10 hover:bg-white/[0.08] hover:border-white/20 focus:outline-none"
                  >
                    <div className="text-base mb-1">{tier.emoji}</div>
                    <div className="text-xs font-bold font-subheading uppercase tracking-wide text-white/80">
                      {tier.label}
                    </div>
                    <div className="text-lg font-heading mt-1 text-white">
                      +${tip}
                    </div>
                    <div className="text-[10px] text-white/60 mt-0.5">
                      {Math.round(tier.pct * 100)}% &middot; {tier.note}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="text-[10px] text-white/40 mt-3 italic">
              Tap a tier to see your total budget with tip. Amounts based on the midpoint of your estimate (${midpoint}).
            </p>
          </motion.div>
        ) : (
          /* ── Selected view ── */
          <motion.div
            key="tier-selected"
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.2 }}
          >
            {/* Selected tier card */}
            <div
              className="rounded-lg p-4 border bg-accent/10 border-accent/40 flex items-center gap-4"
              data-testid={`tip-selected-${selectedTier.label.toLowerCase().replace(" ", "-")}`}
            >
              <span className="text-2xl">{selectedTier.emoji}</span>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold font-subheading uppercase tracking-wide text-violet-300">
                  {selectedTier.label}
                </div>
                <div className="text-[10px] text-white/50 mt-0.5">
                  {Math.round(selectedTier.pct * 100)}% &middot; {selectedTier.note}
                </div>
              </div>
              <div className="text-xl font-heading text-white shrink-0">
                +${selectedTip}
              </div>
            </div>

            {/* Budget breakdown */}
            <div
              className="mt-3 rounded-lg px-4 py-3 bg-white/[0.04] border border-white/10"
              data-testid="tip-budget-total"
            >
              <div className="flex items-center gap-1.5 mb-2">
                <Calculator size={12} className="text-accent" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-accent font-subheading font-bold">
                  Budget to bring
                </span>
              </div>
              <div className="space-y-1 text-xs text-white/60">
                <div className="flex justify-between gap-8">
                  <span>Estimate midpoint</span>
                  <span className="text-white/80">${midpoint}</span>
                </div>
                <div className="flex justify-between gap-8">
                  <span>{selectedTier.label} tip ({Math.round(selectedTier.pct * 100)}%)</span>
                  <span className="text-white/80">+${selectedTip}</span>
                </div>
                <div className="flex justify-between gap-8 pt-1.5 mt-1 border-t border-white/10">
                  <span className="font-bold text-white/90 uppercase tracking-wide font-subheading text-[10px]">Total to budget</span>
                  <span className="font-heading text-base text-white leading-none">${budgetTotal}</span>
                </div>
              </div>
            </div>

            {/* Back button */}
            <button
              data-testid="button-tip-change"
              onClick={() => setSelectedPct(null)}
              className="mt-3 inline-flex items-center justify-center gap-1.5 bg-accent text-white hover:bg-accent/80 transition-colors uppercase tracking-widest text-xs font-bold font-subheading px-5 py-2.5 rounded-full"
            >
              <ArrowLeft size={12} />
              Change tier
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
